import React, { useMemo } from 'react';
import { z } from 'zod';

import { darken, getLuminance, lighten } from '@mui/material/styles';
import castPercentageValue from '@wp-block-builder/block-helpers/castPercentageValue';
import { useGlobalLinkColor } from '@wp-block-builder/stores/WaypointDocumentStore/useGlobalStyles';
import { useEvalLiquid } from '@wp-block-builder/stores/WaypointRendererStore';

import createBoundNumberParser from '../block-helpers/createBoundNumberParser';

import defineBlock from './helpers/defineBlock';

export const ProgressBarBlockSchema = defineBlock(
  'ProgressBar',
  z.object({
    percentageTemplate: z.string().nullish(),
    barColor: z.string().nullish(),
    trackColor: z.string().nullish(),
    barHeight: z.number().nullish(),
    barShape: z.enum(['rectangle', 'rounded', 'pill']).nullish(),
  })
);
export type TProgressBarBlock = z.infer<typeof ProgressBarBlockSchema>;

export const BAR_HEIGHT_OPTIONS = { min: 4, max: 48, defaultValue: 24 };
const barHeightParser = createBoundNumberParser(BAR_HEIGHT_OPTIONS);

function getRoundedCorners(value: unknown) {
  switch (value) {
    case 'rectangle':
      return '0';
    case 'rounded':
      return '4px';
    case 'pill':
    default:
      return '64px';
  }
}

const usePropValues = (block: TProgressBarBlock) => {
  const linkColor = useGlobalLinkColor();
  const barColor = block.props?.barColor ?? linkColor;
  return useMemo(
    () => ({
      barHeight: barHeightParser.parse(block.props?.barHeight),
      borderRadius: getRoundedCorners(block.props?.barShape),
      barColor,
      trackColor:
        block.props?.trackColor ?? (getLuminance(barColor) > 0.5 ? darken(barColor, 0.5) : lighten(barColor, 0.5)),
    }),
    [block, barColor]
  );
};

type ProgressBarBlockProps = {
  block: TProgressBarBlock;
};
export default function ProgressBarBlock({ block }: ProgressBarBlockProps) {
  const template = block.props?.percentageTemplate ?? '';
  const evalLiquid = useEvalLiquid();
  const percentage = useMemo<number>(() => {
    const val = evalLiquid(template);
    return castPercentageValue(val);
  }, [template, evalLiquid]);

  const { barHeight, borderRadius, barColor, trackColor } = usePropValues(block);
  return (
    <table style={{ overflow: 'hidden', borderRadius, border: 0 }} cellPadding={0} cellSpacing={0} width="100%">
      <tbody>
        <tr>
          <td style={{ width: `${percentage}%`, backgroundColor: barColor, height: barHeight }} />
          <td style={{ width: `${100 - percentage}%`, backgroundColor: trackColor, height: barHeight }} />
        </tr>
      </tbody>
    </table>
  );
}
